import { useState, useEffect, useRef } from "react";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Progress } from "@/components/ui/progress";
import { TrendingDown, Users, DollarSign, Award, Calculator, Zap, Target, ArrowRight, Building2, MapPin, FileText, Info } from "lucide-react";
import { getAllCities, getCityById, type CityData } from "@/data/cityData";



type SimulationResults = {
  recidivismReduction: number;
  publicCostSavings: number;
  roiPercentage: number;
  city?: CityData;
  breakdown?: {
    participantsServed: number;
    successfulParticipants: number;
    programSuccessRate: number;
    recidivistsAvoided: number;
    programCost: number;
    costSavingsPerPerson: number;
    programCostPerPerson: number;
  };
  scenarioLabel?: string;
};

const supportTypeOptions = [
  { value: "none", label: "No Support", description: "Basic job placement only" },
  { value: "training", label: "Job Training", description: "Skills development and certification" },
  { value: "housing", label: "Housing Support", description: "Stable housing assistance" },
  { value: "mentoring", label: "Mentoring", description: "Ongoing career guidance" },
  { value: "full_wraparound", label: "Full Wraparound", description: "Comprehensive support services" }
];

const timeframeOptions = [
  { value: "1", label: "1 Year", multiplier: 1 },
  { value: "3", label: "3 Years", multiplier: 3 },
  { value: "5", label: "5 Years", multiplier: 5 },
  { value: "10", label: "10 Years", multiplier: 10 }
];

export default function EconomicImpactSimulator() {
  const [selectedCityId, setSelectedCityId] = useState<string>("");
  const [supportType, setSupportType] = useState<string>("training");
  const [participantCount, setParticipantCount] = useState([1000]);
  const [timeframe, setTimeframe] = useState([1]);
  const [results, setResults] = useState<SimulationResults | null>(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const resultsRef = useRef<HTMLDivElement>(null);

  // Use hardcoded city data instead of API
  const cities = getAllCities();
  const citiesLoading = false;
  const citiesError = null;



  // Calculate core impact metrics
  const calculateImpactMetrics = (data: any) => {
    return {
      participantsServed: data.breakdown?.participantsServed || 0,
      recidivistsAvoided: data.breakdown?.recidivistsAvoided || 0,
      programCost: data.breakdown?.programCost || 0,
      costSavingsPerPerson: data.breakdown?.costSavingsPerPerson || 0
    };
  };

  // Calculate simulation results locally
  const calculateSimulationResults = (params: {
    cityId: number;
    supportType: string;
    participantCount: number;
    timeframe: number;
  }): SimulationResults => {
    const cityData = getCityById(params.cityId);
    if (!cityData) {
      throw new Error('City not found');
    }

    // Calculate program success rate based on support type
    const supportMultipliers = {
      "none": 0.35,
      "training": 0.55,
      "housing": 0.65,
      "mentoring": 0.70,
      "full_wraparound": 0.85
    };
    
    const programSuccessRate = supportMultipliers[params.supportType as keyof typeof supportMultipliers] || 0.55;
    
    // Calculate cost per participant based on support type
    const costPerParticipant = {
      "none": 500,
      "training": 2500,
      "housing": 8000,
      "mentoring": 3500,
      "full_wraparound": 12000
    };
    
    const programCostPerPerson = costPerParticipant[params.supportType as keyof typeof costPerParticipant] || 2500;
    
    // Core calculations
    const participantsServed = params.participantCount;
    const successfulParticipants = Math.floor(participantsServed * programSuccessRate);
    const recidivistsAvoided = Math.floor(successfulParticipants * cityData.baselineRecidivismRate);
    const programCost = participantsServed * programCostPerPerson * params.timeframe;
    const costSavingsPerPerson = cityData.averageCostPerIncarceration * 0.7;
    const totalCostSavings = recidivistsAvoided * costSavingsPerPerson * params.timeframe;
    
    const recidivismReduction = participantsServed > 0 ? (recidivistsAvoided / participantsServed) * 100 : 0;
    const publicCostSavings = totalCostSavings;
    const roiPercentage = programCost > 0 ? ((totalCostSavings - programCost) / programCost) * 100 : 0;

    return {
      recidivismReduction,
      publicCostSavings,
      roiPercentage,
      city: cityData,
      breakdown: {
        participantsServed,
        successfulParticipants,
        programSuccessRate: programSuccessRate * 100,
        recidivistsAvoided,
        programCost,
        costSavingsPerPerson,
        programCostPerPerson
      }
    };
  };

  const selectedCity = cities?.find((c: CityData) => c.id === parseInt(selectedCityId));
  
  const runSimulation = () => {
    if (!selectedCity) return;
    
    setIsCalculating(true);
    
    try {
      const simulationData = calculateSimulationResults({
        cityId: selectedCity.id,
        supportType,
        participantCount: participantCount[0],
        timeframe: timeframe[0]
      });
      
      setResults({ ...simulationData, city: selectedCity });
      setIsCalculating(false);
      
      // Scroll to results after a brief delay
      setTimeout(() => {
        resultsRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } catch (error) {
      console.error('Simulation error:', error);
      setIsCalculating(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      notation: 'compact',
      maximumFractionDigits: 1
    }).format(value);
  };

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      notation: 'compact',
      maximumFractionDigits: 1
    }).format(value);
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Navbar />
      
      <main className="flex-grow">
        {/* Hero Section */}
        <section className="py-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-900 via-blue-800 to-blue-700 text-white">
          <div className="max-w-6xl mx-auto text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Economic Impact Simulator
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 mb-8 max-w-3xl mx-auto">
              Calculate the economic benefits of second-chance employment programs with audit-ready, defensible metrics
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-sm">
              <Badge variant="secondary" className="bg-blue-800 text-blue-100">
                <Calculator className="w-4 h-4 mr-2" />
                Audit-Ready Calculations
              </Badge>
              <Badge variant="secondary" className="bg-blue-800 text-blue-100">
                <FileText className="w-4 h-4 mr-2" />
                Evidence-Based Research
              </Badge>
              <Badge variant="secondary" className="bg-blue-800 text-blue-100">
                <Target className="w-4 h-4 mr-2" />
                Policy Decision Support
              </Badge>
            </div>
          </div>
        </section>

        {/* Input Controls */}
        <section className="py-12 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-slate-900 mb-4">Configure Your Analysis</h2>
              <p className="text-lg text-slate-600">
                Set parameters to model the economic impact of your second-chance employment program
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Location Selection */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-blue-600" />
                    Select Location
                  </CardTitle>
                  <CardDescription>
                    Choose a city to analyze local economic conditions and incarceration costs
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Select value={selectedCityId} onValueChange={setSelectedCityId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a city..." />
                    </SelectTrigger>
                    <SelectContent>
                      {cities?.map((city) => (
                        <SelectItem key={city.id} value={city.id.toString()}>
                          {city.name}, {city.state}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  {selectedCity && (
                    <div className="mt-4 p-4 bg-slate-50 rounded-lg space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-600">Population:</span>
                        <span className="font-medium">{formatNumber(selectedCity.population)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600">Annual Releases:</span>
                        <span className="font-medium">{formatNumber(selectedCity.annualReleases)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600">Baseline Recidivism:</span>
                        <span className="font-medium">{(selectedCity.baselineRecidivismRate * 100).toFixed(0)}%</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600">Cost per Incarceration:</span>
                        <span className="font-medium">{formatCurrency(selectedCity.averageCostPerIncarceration)}</span>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Program Configuration */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Target className="w-5 h-5 text-green-600" />
                    Program Configuration
                  </CardTitle>
                  <CardDescription>
                    Define the type and scale of support services to model
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Support Type */}
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-2 block">Support Type</label>
                    <Select value={supportType} onValueChange={setSupportType}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {supportTypeOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            <div>
                              <div className="font-medium">{option.label}</div>
                              <div className="text-xs text-slate-500">{option.description}</div>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Participant Count */}
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-2 block">
                      Number of Participants: {formatNumber(participantCount[0])}
                    </label>
                    <Slider
                      value={participantCount}
                      onValueChange={setParticipantCount}
                      max={5000}
                      min={100}
                      step={50}
                      className="w-full"
                    />
                    <div className="flex justify-between text-xs text-slate-500 mt-1">
                      <span>100</span>
                      <span>5,000</span>
                    </div>
                  </div>

                  {/* Analysis Timeframe */}
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-2 block">Analysis Timeframe</label>
                    <Select value={timeframe[0].toString()} onValueChange={(value) => setTimeframe([parseInt(value)])}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {timeframeOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Run Simulation Button */}
            <div className="text-center mt-8">
              <Button
                onClick={runSimulation}
                disabled={!selectedCity || isCalculating}
                size="lg"
                className="px-8 py-3"
              >
                {isCalculating ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Calculating Impact...
                  </>
                ) : (
                  <>
                    <Calculator className="w-5 h-5 mr-2" />
                    Calculate Economic Impact
                  </>
                )}
              </Button>
            </div>
          </div>
        </section>

        {/* Results Section */}
        {results && (
          <section ref={resultsRef} className="py-12 px-4 sm:px-6 lg:px-8 bg-white">
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-12">
                <div className="inline-flex items-center gap-2 bg-green-100 text-green-800 px-4 py-2 rounded-full mb-4">
                  <Zap className="w-5 h-5" />
                  Analysis Complete
                </div>
                <h2 className="text-3xl font-bold text-slate-900 mb-4">
                  Projected Economic Impact
                </h2>
                {results && selectedCity && (
                  <p className="text-xl text-slate-600">
                    {selectedCity.name}, {selectedCity.state} • {timeframe[0]} Year Analysis
                  </p>
                )}
              </div>

              {/* Core Impact Metrics */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
                <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200 animate-pulse-once">
                  <CardContent className="p-8">
                    <div className="text-center">
                      <TrendingDown className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                      <p className="text-sm font-medium text-blue-700 mb-2">Recidivism Reduction</p>
                      <p className="text-5xl font-bold text-blue-900 mb-2">
                        {results.recidivismReduction}%
                      </p>
                      <p className="text-sm text-blue-600">
                        {calculateImpactMetrics(results).recidivistsAvoided} fewer repeat offenses
                      </p>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200 animate-pulse-once">
                  <CardContent className="p-8">
                    <div className="text-center">
                      <DollarSign className="w-12 h-12 text-green-600 mx-auto mb-4" />
                      <p className="text-sm font-medium text-green-700 mb-2">Public Cost Savings</p>
                      <p className="text-5xl font-bold text-green-900 mb-2">
                        {formatCurrency(results.publicCostSavings)}
                      </p>
                      <p className="text-sm text-green-600">
                        {formatCurrency(calculateImpactMetrics(results).costSavingsPerPerson)} per person avoided
                      </p>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200 animate-pulse-once">
                  <CardContent className="p-8">
                    <div className="text-center">
                      <Award className="w-12 h-12 text-orange-600 mx-auto mb-4" />
                      <p className="text-sm font-medium text-orange-700 mb-2">Return on Investment</p>
                      <p className="text-5xl font-bold text-orange-900 mb-2">
                        {(results.roiPercentage || 0).toFixed(0)}%
                      </p>
                      <p className="text-sm text-orange-600">
                        Program cost: {formatCurrency(calculateImpactMetrics(results).programCost)}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Program Cost Breakdown */}
              <Card className="bg-slate-50">
                <CardHeader>
                  <CardTitle className="text-xl font-bold text-slate-900">Program Investment Analysis</CardTitle>
                  <p className="text-slate-600">Per-participant cost breakdown and impact metrics</p>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    <div className="space-y-4">
                      <h4 className="font-semibold text-slate-700 flex items-center gap-2">
                        <Users className="w-5 h-5" />
                        Program Success
                      </h4>
                      <div className="space-y-3 text-sm">
                        <div className="flex justify-between">
                          <span className="text-slate-600">Total Participants:</span>
                          <span className="font-medium text-lg">{formatNumber(calculateImpactMetrics(results).participantsServed)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-600">Successful Completions:</span>
                          <span className="font-medium text-lg">{formatNumber(results.breakdown?.successfulParticipants || 0)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-600">Program Success Rate:</span>
                          <span className="font-medium text-green-600">{results.breakdown?.programSuccessRate || 0}%</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-600">Cost per Participant:</span>
                          <span className="font-medium">{formatCurrency((calculateImpactMetrics(results).programCost / calculateImpactMetrics(results).participantsServed) || 0)}</span>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <h4 className="font-semibold text-slate-700 flex items-center gap-2">
                        <TrendingDown className="w-5 h-5" />
                        Crime Reduction
                      </h4>
                      <div className="space-y-3 text-sm">
                        <div className="flex justify-between">
                          <span className="text-slate-600">Recidivists Avoided:</span>
                          <span className="font-medium text-lg">{formatNumber(calculateImpactMetrics(results).recidivistsAvoided)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-600">Savings per Person Avoided:</span>
                          <span className="font-medium">{formatCurrency(calculateImpactMetrics(results).costSavingsPerPerson)}</span>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <h4 className="font-semibold text-slate-700 flex items-center gap-2">
                        <Calculator className="w-5 h-5" />
                        Total Investment
                      </h4>
                      <div className="space-y-3 text-sm">
                        <div className="flex justify-between">
                          <span className="text-slate-600">Total Program Cost:</span>
                          <span className="font-medium text-lg">{formatCurrency(calculateImpactMetrics(results).programCost)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-600">Return on Investment:</span>
                          <span className="font-medium text-green-600">{(results?.roiPercentage || 0).toFixed(0)}%</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </section>
        )}

        {/* Data Sources */}
        <section className="py-12 px-4 sm:px-6 lg:px-8 bg-slate-100">
          <div className="max-w-4xl mx-auto text-center">
            <h3 className="text-xl font-bold text-slate-900 mb-4">Data Sources & Methodology</h3>
            <p className="text-slate-600 mb-6">
              Our simulations are based on peer-reviewed research and official statistics from:
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-slate-600">
              <div>Bureau of Justice Statistics</div>
              <div>Urban Institute</div>
              <div>RAND Corporation</div>
              <div>State Corrections Departments</div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}